import type {
  McpServer,
  RegisteredResource,
  // eslint-disable-next-line import/extensions
} from '@modelcontextprotocol/sdk/server/mcp.js';
import type { Core, Modules } from '@strapi/types';
import { McpCapabilityDefinitionRegistry } from './internal/McpCapabilityDefinitionRegistry';
import {
  type McpCapabilityRegistry,
  McpCapabilityRegistryBase,
} from './internal/McpCapabilityRegistry';
import { createSafeCapabilityRegistration } from './utils/createSafeCapabilityRegistration';
import { wrapCapabilityHandlerForMetrics } from './metrics/wrapCapabilityHandlerForMetrics';

export const makeMcpResourceDefinition = <Definition extends Modules.MCP.McpResourceDefinition>(
  resource: Definition
): Definition => resource;

export class McpResourceRegistry
  extends McpCapabilityRegistryBase<
    'resource',
    Modules.MCP.McpResourceDefinition,
    RegisteredResource
  >
  implements McpCapabilityRegistry
{
  #strapi: Core.Strapi;

  constructor(ctx: {
    strapi: Core.Strapi;
    definitions: McpCapabilityDefinitionRegistry<'resource', Modules.MCP.McpResourceDefinition>;
  }) {
    super(ctx.definitions);
    this.#strapi = ctx.strapi;
  }

  bind(mcpServer: McpServer) {
    const strapi = this.#strapi;

    super.register((definition) => {
      const { name, uri, metadata, createHandler } = definition;

      return createSafeCapabilityRegistration({
        strapi,
        capabilityType: 'Resource',
        name,
        createHandler,
        createFallbackHandler(errorMessage) {
          return async (resourceUri) => ({
            contents: [
              {
                uri: resourceUri.href,
                text: `Resource "${name}" failed to initialize: ${errorMessage}`,
                mimeType: 'text/plain',
              },
            ],
          });
        },
        createErrorResult(error, args) {
          return {
            contents: [
              {
                uri: args[0] instanceof URL ? args[0].href : uri,
                text: `Resource "${name}" execution failed: ${error.message}`,
                mimeType: 'text/plain',
              },
            ],
          };
        },
        registerWithSdk(safeHandler) {
          const sdkHandler = wrapCapabilityHandlerForMetrics(
            strapi,
            'resource',
            name,
            definition.telemetry,
            safeHandler
          );

          return mcpServer.registerResource(name, uri, metadata, sdkHandler);
        },
      });
    });
  }
}
